import React from "react";
import { clsx } from "clsx";
import dynamic from "next/dynamic";
import { Placeholder, StorythreadTout } from "@bloomberg/media-ui-article";

import BasicContent from "~components/articles/basic/BasicContent";
import BasicHeader from "~components/articles/basic/BasicHeader";
import BasicLede from "~components/articles/basic/BasicLede";
import BasicLeftRail from "~components/articles/basic/BasicLeftRail";
import ErrorBoundary from "~components/ErrorBoundary";
import { GridFull } from "~components/GridLayout";
import RightRail from "~components/RightRail";
import ViewportComponent from "~components/ViewportComponent";
import type { ArticleComponentProps, Storythread } from "~types/types";
import isEnabled from "~utils/features";
import { useTranslateMessage } from "~utils/i18n/translate";

import styles from "./Basic.module.css";

const loading = () => (
    <div className="h-[250px]">
        <Placeholder.Image />
    </div>
);
const RecircModule = dynamic(() => import("~components/RecircModule"), {
    ssr: false,
    loading,
});

const ForYouModule = dynamic(
    () => import("~components/ForYou").then((mod) => mod.ForYouModule),
    { loading }
);

const BOA_RECIRC_ENABLED = isEnabled("enableBoaRecirc");

interface StorythreadProps {
    storythread: Storythread;
    className?: string;
}

export function Storythread({ storythread, className }: StorythreadProps) {
    const { headline, id, label, url } = storythread;
    const translateMessage = useTranslateMessage();

    const subtitle =
        label ||
        translateMessage({
            id: "whatYouNeedToKnow",
            defaultMessage: "What you need to know",
        });

    return (
        <StorythreadTout
            className={clsx(styles.storyThread, className)}
            headline={headline}
            storyId={id}
            subtitle={subtitle}
            url={url}
        />
    );
}

export default function Basic({ story }: ArticleComponentProps) {
    return (
        <>
            {/* TODO [ENG40AUD-813] Add labels*/}
            <ErrorBoundary fallback="Failed to render header">
                <BasicHeader />
            </ErrorBoundary>

            <ErrorBoundary fallback="Failed to render lede">
                <BasicLede />
            </ErrorBoundary>

            <ErrorBoundary fallback="Failed to render left rail">
                <BasicLeftRail />
            </ErrorBoundary>

            <ErrorBoundary fallback="failed to render body of article">
                <BasicContent />
            </ErrorBoundary>

            <ErrorBoundary fallback="Failed to render right rail">
                <RightRail />
            </ErrorBoundary>

            {BOA_RECIRC_ENABLED && (
                <ErrorBoundary fallback="">
                    <GridFull name="recirc">
                        <ViewportComponent
                            showPlaceholder
                            onRender={(inViewport) =>
                                inViewport && (
                                    <RecircModule
                                        id={story.id}
                                        resourceType={story.resourceType || ""}
                                        url={story.url}
                                    />
                                )
                            }
                        />

                        <ViewportComponent
                            showPlaceholder
                            wrapperClasses="tup:hidden"
                            onRender={(inViewport) =>
                                inViewport && <ForYouModule />
                            }
                        />
                    </GridFull>
                </ErrorBoundary>
            )}
        </>
    );
}
